/**
 * City-pair landing pages — single source of truth for `/flights/:slug` routes.
 *
 * Each route renders auto-composed copy at /flights/{origin}-to-{destination},
 * e.g. /flights/jfk-to-bkk. Slugs are lowercase IATA codes joined by `-to-`.
 *
 * Adding a new origin hub: append to ORIGIN_HUBS. The sitemap generator,
 * RoutePage, and search-engine index all pick it up automatically.
 */
import { DESTINATION_AIRPORT } from "@/data/blogs";

// US gateway airports we actively serve. Order matters — first entry is the
// default origin used by blog deep-links and inline CTAs.
export const ORIGIN_HUBS = [
  { code: "JFK", city: "New York",     region: "East Coast",  nickname: "the Big Apple" },
  { code: "LAX", city: "Los Angeles",  region: "West Coast",  nickname: "the City of Angels" },
  { code: "MIA", city: "Miami",        region: "Florida",     nickname: "the Magic City" },
];

// Optional per-destination editorial flavour. Falls back to a neutral
// template when missing — every key here is purely additive.
export const DESTINATION_DETAILS = {
  "bkk": { country: "Thailand",       region: "Southeast Asia",   season: "Nov–Feb",  avg_hours: { JFK: 18, LAX: 17, MIA: 21 } },
  "cmb": { country: "Sri Lanka",      region: "South Asia",       season: "Dec–Mar",  avg_hours: { JFK: 19, LAX: 20, MIA: 22 } },
  "sgn": { country: "Vietnam",        region: "Southeast Asia",   season: "Dec–Apr",  avg_hours: { JFK: 21, LAX: 18, MIA: 23 } },
  "dxb": { country: "United Arab Emirates", region: "Middle East", season: "Nov–Mar", avg_hours: { JFK: 13, LAX: 16, MIA: 14 } },
  "ams": { country: "Netherlands",    region: "Western Europe",   season: "Apr–Sep",  avg_hours: { JFK: 7,  LAX: 11, MIA: 9  } },
  "mad": { country: "Spain",          region: "Iberia",           season: "Apr–Jun",  avg_hours: { JFK: 7,  LAX: 12, MIA: 9  } },
  "cdg": { country: "France",         region: "Western Europe",   season: "Apr–Jun",  avg_hours: { JFK: 7,  LAX: 11, MIA: 9  } },
  "ath": { country: "Greece",         region: "Mediterranean",    season: "May–Oct",  avg_hours: { JFK: 10, LAX: 14, MIA: 12 } },
  "mnl": { country: "Philippines",    region: "Southeast Asia",   season: "Dec–May",  avg_hours: { JFK: 21, LAX: 16, MIA: 23 } },
  "syd": { country: "Australia",      region: "Oceania",          season: "Sep–Apr",  avg_hours: { JFK: 22, LAX: 15, MIA: 24 } },
  "akl": { country: "New Zealand",    region: "Oceania",          season: "Nov–Mar",  avg_hours: { JFK: 21, LAX: 13, MIA: 23 } },
  "nan": { country: "Fiji",           region: "South Pacific",    season: "May–Oct",  avg_hours: { JFK: 18, LAX: 11, MIA: 20 } },
  "cun": { country: "Mexico",         region: "Caribbean Coast",  season: "Dec–Apr",  avg_hours: { JFK: 4,  LAX: 5,  MIA: 2  } },
  "hav": { country: "Cuba",           region: "Greater Antilles", season: "Nov–Apr",  avg_hours: { JFK: 4,  LAX: 6,  MIA: 1  } },
  "mbj": { country: "Jamaica",        region: "Caribbean",        season: "Dec–Apr",  avg_hours: { JFK: 4,  LAX: 7,  MIA: 2  } },
  "osl": { country: "Norway",         region: "Scandinavia",      season: "Jun–Aug · Nov–Mar (aurora)", avg_hours: { JFK: 8, LAX: 12, MIA: 10 } },
  "kef": { country: "Iceland",        region: "Nordic",           season: "Sep–Mar (aurora) · Jun–Aug",  avg_hours: { JFK: 6, LAX: 9,  MIA: 8  } },
  "hel": { country: "Finland",        region: "Scandinavia",      season: "Dec–Mar · Jun–Aug",           avg_hours: { JFK: 9, LAX: 13, MIA: 11 } },
  "arn": { country: "Sweden",         region: "Scandinavia",      season: "May–Sep",  avg_hours: { JFK: 8,  LAX: 12, MIA: 10 } },
  "lhr": { country: "United Kingdom", region: "British Isles",    season: "Year-round", avg_hours: { JFK: 7, LAX: 11, MIA: 9 } },
  "edi": { country: "Scotland",       region: "British Isles",    season: "May–Sep",  avg_hours: { JFK: 7,  LAX: 11, MIA: 9  } },
  "bfs": { country: "Northern Ireland", region: "British Isles",  season: "May–Sep",  avg_hours: { JFK: 7,  LAX: 11, MIA: 9  } },
  "cwl": { country: "Wales",          region: "British Isles",    season: "May–Sep",  avg_hours: { JFK: 8,  LAX: 12, MIA: 10 } },
};

/**
 * Editorial copy for the per-destination landing pages (/destinations/:code).
 * Lightweight by design — `tagline` is one short line, `description` two
 * sentences max. Hero image references the related blog post's composite via
 * BLOG_HERO_* (resolved lazily in DestinationPage).
 */
export const DESTINATION_EDITORIAL = {
  "bkk": { city: "Bangkok",      tagline: "Glittering temples, neon nights, and the world's best street food.",                          best_for: ["Street food", "Temples", "Tropical nights"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/2j63yn9m_Bangkok.png" },
  "cmb": { city: "Colombo",      tagline: "The lush, layered gateway to Sri Lanka's tea hills and beaches.",                              best_for: ["Tea country", "Coastal escapes", "Ayurveda"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/zhpke8up_Colombo.png" },
  "sgn": { city: "Ho Chi Minh",  tagline: "Energy, espresso, and motorbikes — Vietnam's southern engine.",                                 best_for: ["Coffee culture", "Vietnamese cuisine", "Modern history"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/cfbkkg17_Ho%20Chi%20Minh.png" },
  "dxb": { city: "Dubai",        tagline: "Sky-scraping ambition above the Arabian Gulf.",                                                  best_for: ["Luxury stays", "Desert experiences", "Layover stopovers"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/p9wmrh3w_Dubai.png" },
  "ams": { city: "Amsterdam",    tagline: "Canals, Cuyp, and the most civilised cycling city in Europe.",                                  best_for: ["Art & museums", "Canal walks", "Long-weekend escapes"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/ptoy97d7_Amsterdam.png" },
  "mad": { city: "Madrid",       tagline: "Late dinners, Velázquez at the Prado, and roof-terrace nights.",                                best_for: ["Spanish dining", "Art galleries", "City breaks"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/v6vxoq8f_Madrid%20image.png" },
  "ath": { city: "Athens",       tagline: "The Acropolis above a city that's quietly cool again.",                                          best_for: ["Ancient history", "Mediterranean food", "Island gateway"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/bqzavysg_Athens.png" },
  "mnl": { city: "Manila",       tagline: "Your starting line for 7,000 islands of Filipino warmth.",                                       best_for: ["Island-hopping", "Filipino cuisine", "Diving"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/sr5ostht_Manila.png" },
  "syd": { city: "Sydney",       tagline: "Harbour, beaches, and a skyline that earns the hype.",                                            best_for: ["Coastal walks", "Harbour life", "Outdoor culture"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/89pqn13o_Sydney.png" },
  "akl": { city: "Auckland",     tagline: "City of sails — the gateway to fjords, glaciers, and Hobbit country.",                          best_for: ["Adventure travel", "South Island circuits", "Wine country"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/5oijfpm6_Auckland.png" },
  "nan": { city: "Nadi",         tagline: "Tropical luxury, soft-sand beaches, and Bula warmth.",                                            best_for: ["Island resorts", "Honeymoons", "Snorkelling"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/eycpkshz_Nadi.png" },
  "cun": { city: "Cancun",       tagline: "Caribbean turquoise, Mayan ruins, and easy long-weekend escapes.",                                best_for: ["Family beaches", "Mayan ruins", "All-inclusive"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/cq1awkm3_Cancun.png" },
  "hav": { city: "Havana",       tagline: "Vintage cars, salsa nights, and Caribbean light unlike anywhere else.",                          best_for: ["Cultural travel", "Music & dance", "Colonial architecture"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/3e0yucdw_Havana.png" },
  "mbj": { city: "Montego Bay",  tagline: "Reggae, white sand, and one of the Caribbean's friendliest welcomes.",                            best_for: ["Beach resorts", "Reggae culture", "Snorkelling"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/qsqw5qc7_Montego%20Bay.png" },
  "osl": { city: "Oslo",         tagline: "Fjord access, museums, and design-first Scandinavian cool.",                                       best_for: ["Fjord cruises", "Aurora chasing", "Design"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/0efcz1uw_Oslo.png" },
  "kef": { city: "Reykjavik",    tagline: "Iceland's gateway — fire, ice, and geothermal lagoons within an hour.",                            best_for: ["Northern Lights", "Golden Circle", "Hot springs"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/v6b7c4gq_reykjavik.png" },
  "hel": { city: "Helsinki",     tagline: "Saunas, design, and the quiet confidence of the world's happiest country.",                       best_for: ["Sauna culture", "Lapland gateway", "Nordic design"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/1r82mzl1_Helsinki.png" },
  "arn": { city: "Stockholm",    tagline: "The Venice of the North, built across fourteen islands.",                                          best_for: ["Archipelago days", "Museums", "Scandi food"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/gbft6np0_Stockholm.png" },
  "lhr": { city: "London",       tagline: "Royal heritage, world-class theatre, and timeless luxury — all in one city.",                     best_for: ["Theatre & culture", "Luxury shopping", "Royal history"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/73j8ie32_London.png" },
  "edi": { city: "Edinburgh",    tagline: "Medieval old town, Georgian new town, and a castle on a volcanic rock.",                          best_for: ["Whisky country", "Festival season", "Highland trips"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/11dn4rwf_Edinburgh.png" },
  "bfs": { city: "Belfast",      tagline: "Maritime history, dramatic coast, and Game-of-Thrones country at the door.",                      best_for: ["Causeway Coast", "Maritime heritage", "Film locations"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/1dmt9yeh_Belfast.png" },
  "cwl": { city: "Cardiff",      tagline: "A compact capital with castles, coastline, and Snowdonia at the door.",                            best_for: ["Castles", "Snowdonia hiking", "Welsh culture"], img: "https://customer-assets.emergentagent.com/job_flight-booking-hub-21/artifacts/528fqqer_Cardiff.png" },
};

/** Map destination anchor (e.g. "thailand") to the matching blog post slug. */
const BLOG_BY_DEST = {
  "sri-lanka":        "asias-most-captivating-destinations",
  "vietnam":          "asias-most-captivating-destinations",
  "dubai":            "asias-most-captivating-destinations",
  "thailand":         "asias-most-captivating-destinations",
  "netherlands":      "europes-timeless-treasures",
  "spain":            "europes-timeless-treasures",
  "france":           "europes-timeless-treasures",
  "greece":           "europes-timeless-treasures",
  "philippines":      "escape-to-paradise-pacific",
  "australia":        "escape-to-paradise-pacific",
  "new-zealand":      "escape-to-paradise-pacific",
  "nadi":             "escape-to-paradise-pacific",
  "mexico":           "magic-of-mexico-cuba-caribbean",
  "cuba":             "magic-of-mexico-cuba-caribbean",
  "caribbean":        "magic-of-mexico-cuba-caribbean",
  "norway":           "wonders-of-scandinavia",
  "iceland":          "wonders-of-scandinavia",
  "finland":          "wonders-of-scandinavia",
  "sweden":           "wonders-of-scandinavia",
  "london":           "best-of-the-united-kingdom",
  "scotland":         "best-of-the-united-kingdom",
  "northern-ireland": "best-of-the-united-kingdom",
  "wales":            "best-of-the-united-kingdom",
};

const ORIGIN_BY_CODE = Object.fromEntries(ORIGIN_HUBS.map((h) => [h.code, h]));

/** Build the URL slug used in `/flights/:slug`. */
export function buildRouteSlug(originCode, destCode) {
  return `${originCode.toLowerCase()}-to-${destCode.toLowerCase()}`;
}

/** Reverse: parse a slug back to { origin, destination } codes (uppercase). */
export function parseRouteSlug(slug) {
  const m = /^([a-z]{3})-to-([a-z]{3})$/i.exec(slug || "");
  if (!m) return null;
  return { originCode: m[1].toUpperCase(), destCode: m[2].toUpperCase() };
}

/** Resolve a route slug into a fully-composed page model — or null if invalid. */
export function getRouteData(slug) {
  const parsed = parseRouteSlug(slug);
  if (!parsed) return null;
  const origin = ORIGIN_BY_CODE[parsed.originCode];
  if (!origin) return null;

  const destEntry = Object.entries(DESTINATION_AIRPORT).find(
    ([, v]) => v.code === parsed.destCode
  );
  if (!destEntry) return null;
  const [destAnchor, dest] = destEntry;

  const details = DESTINATION_DETAILS[parsed.destCode.toLowerCase()] || {};
  const blogSlug = BLOG_BY_DEST[destAnchor] || null;
  const flightHours = details.avg_hours?.[origin.code];

  return {
    slug: buildRouteSlug(origin.code, dest.code),
    origin,
    destination: { ...dest, anchor: destAnchor, country: details.country, region: details.region },
    flightHours,
    bestSeason: details.season,
    blogSlug,
  };
}

/** All valid routes — used by the sitemap and any future "popular routes" UI. */
export function listAllRoutes() {
  const out = [];
  for (const hub of ORIGIN_HUBS) {
    for (const [anchor, dest] of Object.entries(DESTINATION_AIRPORT)) {
      if (hub.code === dest.code) continue;
      out.push({ originCode: hub.code, destCode: dest.code, anchor, slug: buildRouteSlug(hub.code, dest.code) });
    }
  }
  return out;
}

/**
 * Resolve a fully-composed destination page model from an IATA code.
 * Pulls country/region/season/flight-hours from DESTINATION_DETAILS,
 * tagline/best_for from DESTINATION_EDITORIAL, and the hero image from the
 * related blog post (already in blogs.js). Returns null for unknown codes.
 */
export function getDestinationContent(code, { blogPosts } = {}) {
  if (!code) return null;
  const key = code.toLowerCase();
  const details   = DESTINATION_DETAILS[key];
  const editorial = DESTINATION_EDITORIAL[key];
  if (!details || !editorial) return null;

  const upper = code.toUpperCase();
  const entry = Object.entries(DESTINATION_AIRPORT).find(([, v]) => v.code === upper);
  const anchor = entry ? entry[0] : null;
  const blogSlug = anchor ? BLOG_BY_DEST[anchor] : null;
  // Prefer per-destination editorial imagery when supplied; fall back to the
  // blog post composite. Keeps editor's intent for special cities.
  const heroImg = editorial.img || (blogSlug && blogPosts?.[blogSlug]?.hero);

  // Pull a one-sentence intro from the relevant blog post's intro section
  // when available. This keeps the editorial voice consistent and means we
  // don't need to author a second copy for every destination.
  let description = "";
  if (blogSlug && blogPosts?.[blogSlug]) {
    const sec = blogPosts[blogSlug].sections.find((s) => s.anchor === anchor);
    if (sec?.paragraphs?.[0]) description = sec.paragraphs[0];
  }

  return {
    code: upper,
    city: editorial.city,
    country: details.country,
    region: details.region,
    season: details.season,
    typical_flight_hours: details.avg_hours?.JFK,
    tagline: editorial.tagline,
    description,
    best_for: editorial.best_for,
    img: heroImg,
    blogSlug,
  };
}

/**
 * All destinations that have a `/destinations/:code` page. Includes the 4
 * featured destinations from content.js AND the 22 auto-composed ones from
 * routes.js. Used by the /destinations index page and the sitemap generator.
 *
 * `featured` entries (from content.js) carry their own bespoke imagery; the
 * caller decides whether to merge them in. Default behaviour: caller passes
 * the featured list via the `featured` arg so this module stays decoupled
 * from content.js.
 */
export function listAllDestinations({ blogPosts, featured = [] } = {}) {
  const out = [];
  const seen = new Set();

  // 1. Featured first (preserves home-page tile order — Santorini, Manarola, Paris, Tashkent).
  for (const f of featured) {
    const upper = f.code.toUpperCase();
    if (seen.has(upper)) continue;
    out.push({
      code: upper,
      city: f.city,
      country: f.country,
      region: f.region,
      tagline: f.tagline,
      best_for: f.best_for,
      img: f.img,
      featured: true,
    });
    seen.add(upper);
  }

  // 2. Auto-composed from the editorial map, grouped by blog region for nice
  //    ordering in the UI.
  const groupedByBlog = {};
  for (const [code] of Object.entries(DESTINATION_EDITORIAL)) {
    const upper = code.toUpperCase();
    if (seen.has(upper)) continue;
    const data = getDestinationContent(upper, { blogPosts });
    if (!data) continue;
    const groupKey = data.blogSlug || "other";
    (groupedByBlog[groupKey] ||= []).push(data);
    seen.add(upper);
  }
  // Preserve insertion order of blog groups by reading from BLOG_BY_DEST values.
  const blogOrder = [...new Set(Object.values(BLOG_BY_DEST))];
  for (const slug of blogOrder) {
    if (groupedByBlog[slug]) out.push(...groupedByBlog[slug]);
  }
  if (groupedByBlog.other) out.push(...groupedByBlog.other);

  return out;
}

/**
 * Return up to `limit` sibling destinations that share the same blog post
 * grouping (i.e. the same regional editorial — Asia, Europe, Pacific, etc.).
 * Used for "Discover more in {region}" cross-link grid on the destination
 * editorial page. Excludes the input destination itself.
 */
export function getRelatedDestinations(code, { blogPosts, limit = 3 } = {}) {
  if (!code) return [];
  const upper = code.toUpperCase();

  // Find this destination's blog grouping.
  const entry = Object.entries(DESTINATION_AIRPORT).find(([, v]) => v.code === upper);
  if (!entry) return [];
  const [anchor] = entry;
  const blogSlug = BLOG_BY_DEST[anchor];
  if (!blogSlug) return [];

  // Collect every other destination in the same blog grouping.
  const siblings = [];
  for (const [otherAnchor, otherBlog] of Object.entries(BLOG_BY_DEST)) {
    if (otherBlog !== blogSlug) continue;
    if (otherAnchor === anchor) continue;
    const data = getDestinationContent(DESTINATION_AIRPORT[otherAnchor]?.code, { blogPosts });
    if (data) siblings.push(data);
    if (siblings.length >= limit) break;
  }
  return siblings;
}
